/* @moldura padrao */
/**
 * form.tsx (ordens-compra) — Lançar e mandar ao Tiny uma ordem de compra (ficha #42).
 *
 * TRÊS ABAS (dele, 28/09: "gosto de aba, fica menos bagunçado"):
 *   - Compra  — o que vai ao Tiny: loja, data, total, fornecedor, categoria,
 *               parcelas (opcionais) e observações;
 *   - Pedidos — o que é só nosso: a parte da OC que é de cada pedido de venda.
 *               O nº é opcional (o pedido pode ainda não estar no Tiny); com nº,
 *               o cliente vem do espelho (ficha #44); sem, digita-se. A próxima
 *               linha já nasce com o que falta para fechar o total;
 *   - Tiny    — o corpo que vai, e o botão de mandar.
 *
 * No Tiny a OC vai com UM item, o "Item AOC", e os pedidos vão escritos nas
 * observações internas (a v3 não aceita item de texto livre — teste de 27/09).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { hojeISO, formatarData } from '@/lib/data';
import { doBanco, paraConta } from '@/lib/numero';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { CustomCampoData } from '@/components/CustomCamposData';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomCampoFornecedorTiny } from '@/components/CustomCamposFornecedor';
import { fmt } from '../shared';
import type { OrdemCompra } from './shared';
import { ROTULO_ENVIO } from './shared';

interface Loja {
  id: number;
  codigo: string | null;
  nome: string;
  nome_curto?: string | null;
}

interface Opcoes {
  categorias: { id: string; nome: string; grupo: string | null }[];
  contatos: { id: string; nome: string }[];
}

interface LinhaPedido {
  numero: string;
  pedido_id: number | null;
  cliente: string;
  /** o cliente veio do pedido do Tiny (não se digita por cima) */
  clienteDoTiny: boolean;
  valor: string; // canônico ('' | '800.00')
  descricao: string;
  buscando?: boolean;
  aviso?: string | null;
}

interface LinhaParcela {
  vencimento: string;
  valor: string;
}

/** Categoria de despesa: a v3 não diz o tipo, só o grupo — receitas e entradas saem. */
const ehReceita = (grupo: string | null) => /^(receitas|outras entradas)/i.test((grupo ?? '').trim());

const centavos = (v: string) => Math.round(paraConta(v) * 100);
const deCentavos = (c: number) => (c > 0 ? doBanco(c / 100, 2, 'oc.valor') : '');
const linhaVazia = (valor = ''): LinhaPedido => ({
  numero: '', pedido_id: null, cliente: '', clienteDoTiny: false, valor, descricao: '',
});

type Aba = 'compra' | 'pedidos' | 'tiny';

interface Previa {
  payload: unknown;
  falta: string | null;
  /** o que precisa estar pronto para mandar — a aba Tiny mostra item a item */
  pronto: { item: string; ok: boolean }[];
  escrita_ativa: boolean;
}

export function OrdemCompraFormPage() {
  const { id } = useParams();
  const editando = !!id && /^\d+$/.test(id);
  const navigate = useNavigate();
  const { pode } = useAuth();

  const [aba, setAba] = useState<Aba>('compra');
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [lojaId, setLojaId] = useState('');
  const [data, setData] = useState(hojeISO());
  const [fornecedorId, setFornecedorId] = useState<number | null>(null);
  const [categoria, setCategoria] = useState('');
  const [total, setTotal] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [linhas, setLinhas] = useState<LinhaPedido[]>([linhaVazia()]);
  const [parcelas, setParcelas] = useState<LinhaParcela[]>([]);
  const [opcoes, setOpcoes] = useState<Opcoes>({ categorias: [], contatos: [] });
  const [oc, setOc] = useState<OrdemCompra | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [idTiny, setIdTiny] = useState('');

  // Cadeado = o Tiny não confirmou: nada muda até destravar. Enviada, ela
  // continua editável (dele, 29/09); com CONTA LANÇADA lá, a aba Compra trava
  // e só os pedidos e a justificativa mudam — e só aqui (migration 121).
  const travada = !!oc && (oc.envio_tiny === 'enviando' || oc.envio_tiny === 'verificar');
  const enviada = oc?.envio_tiny === 'enviada';
  const contasLancadas = !!oc?.contas_lancadas_tiny;

  useEffect(() => {
    api.get<Loja[]>('/lojas').then((ls) => {
      setLojas(ls);
      if (!editando && ls.length) setLojaId(String(ls[0].id));
    });
  }, [editando]);

  useEffect(() => {
    if (!lojaId) return;
    api.get<Opcoes>(`/despesas/tiny/opcoes?loja_id=${lojaId}`).then(setOpcoes).catch(() => undefined);
  }, [lojaId]);

  /** Só categorias de DESPESA, agrupadas como no plano de contas do Tiny. */
  const gruposDespesa = useMemo(() => {
    const m = new Map<string, Opcoes['categorias']>();
    for (const c of opcoes.categorias) {
      if (ehReceita(c.grupo)) continue;
      const g = c.grupo || 'Sem grupo';
      m.set(g, [...(m.get(g) ?? []), c]);
    }
    return [...m.entries()];
  }, [opcoes.categorias]);

  function aplicar(o: OrdemCompra) {
    setOc(o);
    setLojaId(String(o.loja_id));
    setData(o.data ?? hojeISO());
    setFornecedorId(o.fornecedor_id ?? null);
    setCategoria(o.categoria_tiny ?? '');
    setTotal(doBanco(o.valor_total, 2, 'oc.total'));
    setObservacoes(o.observacoes ?? '');
    setJustificativa(o.explicacao ?? '');
    setLinhas(
      o.vinculos.length
        ? o.vinculos.map((v) => ({
            numero: v.pedido_numero ?? '',
            pedido_id: v.pedido_id,
            cliente: v.cliente ?? '',
            clienteDoTiny: !!v.pedido_id,
            valor: doBanco(v.valor, 2, 'oc.vinculo'),
            descricao: v.descricao ?? '',
          }))
        : [linhaVazia()],
    );
    setParcelas(o.parcelas.map((p) => ({ vencimento: p.vencimento, valor: doBanco(p.valor, 2, 'oc.parcela') })));
    api
      .get<Previa>(`/custos-pedido/ordens-compra/${o.id}/tiny`)
      .then(setPrevia)
      .catch(() => setPrevia(null));
  }

  useEffect(() => {
    if (!editando) return;
    api
      .get<OrdemCompra>(`/custos-pedido/ordens-compra/${id}`)
      .then(aplicar)
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Não foi possível abrir.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function mudarLinha(i: number, mud: Partial<LinhaPedido>) {
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, ...mud } : l)));
  }

  /** O nº digitado vira pedido do espelho (ficha #44). Não achou? Tudo bem: o
   *  pedido pode ainda não estar no Tiny — o cliente se digita. */
  async function buscarPedido(i: number) {
    const numero = linhas[i].numero.trim();
    if (!numero || !lojaId) {
      mudarLinha(i, { pedido_id: null, clienteDoTiny: false, aviso: null });
      return;
    }
    mudarLinha(i, { buscando: true, aviso: null });
    try {
      const p = await api.get<{ id: number; cliente: string | null }>(
        `/custos-pedido/tiny/buscar?loja_id=${lojaId}&numero=${encodeURIComponent(numero)}`,
      );
      mudarLinha(i, { pedido_id: p.id, cliente: p.cliente ?? '', clienteDoTiny: true, buscando: false });
    } catch {
      mudarLinha(i, {
        pedido_id: null,
        clienteDoTiny: false,
        buscando: false,
        aviso: 'Não está no Tiny — digite o cliente',
      });
    }
  }

  const totalC = centavos(total);
  const somaPedidosC = linhas.reduce((s, l) => s + centavos(l.valor), 0);
  const faltaC = totalC - somaPedidosC;
  const linhasValidas = linhas.filter((l) => centavos(l.valor) > 0);
  const pedidosFecham = linhasValidas.length > 0 && faltaC === 0;
  const somaParcelasC = parcelas.reduce((s, p) => s + centavos(p.valor), 0);
  const parcelasFecham = parcelas.length === 0 || somaParcelasC === totalC;
  const podeSalvar = !!lojaId && totalC > 0 && pedidosFecham && parcelasFecham && !salvando;

  /** A próxima linha já nasce com o que falta para fechar o total (dele, 28/09). */
  function maisUmPedido() {
    setLinhas((ls) => [...ls, linhaVazia(deCentavos(faltaC))]);
  }

  async function salvar() {
    setSalvando(true);
    const corpo = {
      loja_id: Number(lojaId),
      data,
      fornecedor_id: fornecedorId,
      categoria_tiny: categoria || null,
      valor_total: paraConta(total),
      observacoes: observacoes || null,
      explicacao: justificativa || null,
      vinculos: linhasValidas.map((l) => ({
        pedido_id: l.pedido_id,
        pedido_numero: l.numero || null,
        cliente: l.cliente || null,
        valor: paraConta(l.valor),
        descricao: l.descricao || null,
      })),
      parcelas: parcelas.map((p) => ({ vencimento: p.vencimento, valor: paraConta(p.valor) })),
    };
    try {
      const o = editando
        ? await api.put<OrdemCompra>(`/custos-pedido/ordens-compra/${id}`, corpo)
        : await api.post<OrdemCompra>('/custos-pedido/ordens-compra', corpo);
      toast.success('Ordem de compra salva.');
      if (o.aviso_tiny) (o.sincronia_tiny === 'ok' ? toast.success : toast.warning)(o.aviso_tiny);
      if (!editando) navigate(`/custos-pedido/ordens-compra/${o.id}`, { replace: true });
      else aplicar(o);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function enviar() {
    try {
      aplicar(await api.post<OrdemCompra>(`/custos-pedido/ordens-compra/${id}/tiny/enviar`));
      toast.success('Criada no Tiny.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível enviar.');
      api.get<OrdemCompra>(`/custos-pedido/ordens-compra/${id}`).then(aplicar).catch(() => undefined);
    } finally {
      setConfirmar(false);
    }
  }

  async function reabrir(entrou: boolean) {
    try {
      aplicar(
        await api.post<OrdemCompra>(`/custos-pedido/ordens-compra/${id}/tiny/reabrir`, {
          id_tiny: entrou ? Number(idTiny) : null,
        }),
      );
      setIdTiny('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível abrir o cadeado.');
    }
  }

  const rotulo = ROTULO_ENVIO[oc?.envio_tiny ?? 'nao_enviada'] ?? ROTULO_ENVIO.nao_enviada;

  /** Por que não dá para salvar, dito ao lado do botão — nunca um botão cinza mudo. */
  const motivo =
    !lojaId ? 'Escolha a loja.'
    : totalC <= 0 ? 'Informe o valor total.'
    : !linhasValidas.length ? 'Falta ao menos um pedido (aba Pedidos).'
    : faltaC !== 0 ? `Os pedidos somam ${fmt(somaPedidosC / 100)} de ${fmt(totalC / 100)}.`
    : !parcelasFecham ? `As parcelas somam ${fmt(somaParcelasC / 100)} de ${fmt(totalC / 100)}.`
    : null;

  /** O padrão do projeto (lei visual §8, 28/09): cada aba que edita tem o seu
   *  Salvar no PÉ, à direita, com o motivo ao lado quando não dá. Salva a OC
   *  inteira — as abas são só arrumação da mesma ficha. */
  const barraSalvar = travada ? null : (
    <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
      {motivo && <span className="small text-secondary">{motivo}</span>}
      <button type="button" className="btn btn-theme" onClick={salvar} disabled={!podeSalvar}>
        {salvando && <span className="spinner-border spinner-border-sm me-2" />}
        Salvar
      </button>
    </div>
  );
  const abas: [Aba, string, string, string | null][] = [
    ['compra', 'Compra', 'bi-cart', null],
    ['pedidos', 'Pedidos', 'bi-receipt', pedidosFecham ? null : 'não fecha'],
    ['tiny', 'Tiny', 'bi-send', null],
  ];

  return (
    <CustomMolduraPagina
      titulo={editando ? `Ordem de compra${oc?.numero_tiny ? ` #${oc.numero_tiny}` : ''}` : 'Nova ordem de compra'}
      descricao={oc?.criado_por ? `lançada por ${oc.criado_por}` : 'Compra que entra no custo dos pedidos'}
      caminho={[{ rotulo: 'Ordens de compra', para: '/custos-pedido/ordens-compra' }]}
      acoes={
        editando ? (
          <span className={`badge ${rotulo.classe}`}>
            <i className={`bi ${rotulo.icone} me-1`}></i>
            {rotulo.texto}
          </span>
        ) : undefined
      }
    >
      <ul className="nav nav-tabs adminuiux-tabs mb-3 mb-lg-4">
        {abas.map(([k, texto, icone, alerta]) => (
          <li className="nav-item" key={k}>
            <button type="button" className={`nav-link ${aba === k ? 'active' : ''}`} onClick={() => setAba(k)}>
              <i className={`bi ${icone} me-1`}></i>
              {texto}
              {alerta && totalC > 0 && <span className="badge casaa-selo-atencao ms-2">{alerta}</span>}
            </button>
          </li>
        ))}
      </ul>

      {aba !== 'tiny' && (travada || enviada) && (
        <p className="small text-secondary">
          <i className={`bi me-1 ${travada || contasLancadas ? 'bi-lock' : 'bi-arrow-repeat'}`}></i>
          {travada
            ? 'Com cadeado: o Tiny não confirmou o envio. Destrave na aba Tiny antes de mudar.'
            : contasLancadas
              ? 'A conta já foi lançada no Tiny: lá esta ordem de compra não muda mais. Aqui mudam só os pedidos e a justificativa.'
              : 'Já está no Tiny: ao salvar, a mudança vai para lá também (enquanto a conta não for lançada).'}
        </p>
      )}

      {aba === 'compra' && (
        <fieldset disabled={travada || contasLancadas}>
          <div className="row g-3">
            <div className="col-lg-8">
              <div className="card adminuiux-card">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-sm-3">
                      <div className="form-floating">
                        <select id="oc-loja" className="form-select" value={lojaId} onChange={(e) => setLojaId(e.target.value)}>
                          {lojas.map((l) => (
                            <option key={l.id} value={String(l.id)}>
                              {l.nome_curto || l.nome}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="oc-loja">Loja</label>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <CustomCampoData id="oc-data" label="Data" valor={data} onChange={setData} />
                    </div>
                    <div className="col-sm-5">
                      <CustomCampoNumeroMoeda id="oc-total" label="Valor total" valor={total} onChange={setTotal} />
                    </div>
                    <div className="col-sm-6">
                      <CustomCampoFornecedorTiny
                        id="oc-fornecedor"
                        lojaId={lojaId ? Number(lojaId) : null}
                        valor={fornecedorId}
                        onChange={setFornecedorId}
                        desabilitado={travada || contasLancadas}
                      />
                    </div>
                    <div className="col-sm-6">
                      <div className="form-floating">
                        <select id="oc-categoria" className="form-select" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                          <option value="">—</option>
                          {gruposDespesa.map(([grupo, itens]) => (
                            <optgroup key={grupo} label={grupo}>
                              {itens.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.nome}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <label htmlFor="oc-categoria">Categoria de despesa</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          id="oc-observacoes"
                          className="form-control"
                          style={{ height: 70 }}
                          placeholder=" "
                          value={observacoes}
                          onChange={(e) => setObservacoes(e.target.value)}
                        />
                        <label htmlFor="oc-observacoes">Observações</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card adminuiux-card">
                <div className="card-header bg-transparent d-flex align-items-center">
                  <h6 className="mb-0 flex-grow-1">Parcelas</h6>
                  {parcelas.length > 0 && (
                    <span className={`small ${parcelasFecham ? 'text-success' : 'text-danger'}`}>
                      {fmt(somaParcelasC / 100)} de {fmt(totalC / 100)}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {enviada ? (
                    <p className="small text-secondary mb-2">Depois de enviada, as parcelas se acertam no Tiny.</p>
                  ) : parcelas.length === 0 ? (
                    <p className="small text-secondary mb-2">Sem parcelas — o pagamento se define no Tiny.</p>
                  ) : null}
                  <fieldset disabled={enviada}>
                  {parcelas.map((p, i) => (
                    <div className="d-flex gap-2 align-items-center mb-2" key={i}>
                      <div className="flex-grow-1">
                        <CustomCampoData
                          valor={p.vencimento}
                          onChange={(v) => setParcelas((ps) => ps.map((x, j) => (j === i ? { ...x, vencimento: v } : x)))}
                        />
                      </div>
                      <div style={{ width: 140 }}>
                        <CustomCampoNumeroMoeda
                          sm
                          valor={p.valor}
                          onChange={(v) => setParcelas((ps) => ps.map((x, j) => (j === i ? { ...x, valor: v } : x)))}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-secondary p-0"
                        onClick={() => setParcelas((ps) => ps.filter((_, j) => j !== i))}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={() =>
                      setParcelas((ps) => [
                        ...ps,
                        { vencimento: data, valor: deCentavos(totalC - ps.reduce((s, x) => s + centavos(x.valor), 0)) },
                      ])
                    }
                  >
                    <i className="bi bi-plus-lg me-1"></i>Parcela
                  </button>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
          {!contasLancadas && barraSalvar}
        </fieldset>
      )}

      {aba === 'pedidos' && (
        <fieldset disabled={travada}>
          <div className="card adminuiux-card">
            <div className="card-header bg-transparent d-flex align-items-center">
              <h6 className="mb-0 flex-grow-1">Pedidos desta compra</h6>
              <span className={`small ${pedidosFecham ? 'text-success' : 'text-secondary'}`}>
                {fmt(somaPedidosC / 100)} de {fmt(totalC / 100)}
                {pedidosFecham ? (
                  <i className="bi bi-check-lg ms-1"></i>
                ) : totalC > 0 ? (
                  ` · falta ${fmt(faltaC / 100)}`
                ) : null}
              </span>
            </div>
            <div className="card-body">
              {totalC <= 0 && (
                <p className="small text-secondary">Informe o valor total na aba Compra — ele é dividido entre os pedidos.</p>
              )}
              <table className="table table-sm align-middle small mb-2">
                <thead>
                  <tr className="text-secondary text-uppercase">
                    <th style={{ width: 110 }}>Nº pedido</th>
                    <th>Cliente</th>
                    <th style={{ width: 160 }}>Valor</th>
                    <th style={{ width: 70 }} className="text-end">
                      %
                    </th>
                    <th>Descrição</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          inputMode="numeric"
                          placeholder="opcional"
                          value={l.numero}
                          onChange={(e) =>
                            mudarLinha(i, {
                              numero: e.target.value.replace(/\D/g, ''),
                              pedido_id: null,
                              clienteDoTiny: false,
                              aviso: null,
                            })
                          }
                          onBlur={() => buscarPedido(i)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              buscarPedido(i);
                            }
                          }}
                        />
                      </td>
                      <td>
                        {l.buscando ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <>
                            <input
                              className="form-control form-control-sm"
                              value={l.cliente}
                              readOnly={l.clienteDoTiny}
                              placeholder={l.clienteDoTiny ? '' : 'Cliente'}
                              title={l.clienteDoTiny ? 'Vem do pedido no Tiny' : undefined}
                              onChange={(e) => mudarLinha(i, { cliente: e.target.value })}
                            />
                            {l.aviso && <div className="form-text">{l.aviso}</div>}
                          </>
                        )}
                      </td>
                      <td>
                        <CustomCampoNumeroMoeda sm valor={l.valor} onChange={(v) => mudarLinha(i, { valor: v })} />
                      </td>
                      <td className="text-end text-secondary">
                        {totalC > 0 && centavos(l.valor) > 0 ? `${((centavos(l.valor) / totalC) * 100).toFixed(0)}%` : '—'}
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          maxLength={255}
                          value={l.descricao}
                          onChange={(e) => mudarLinha(i, { descricao: e.target.value })}
                        />
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-secondary p-0"
                          title="Tirar esta linha"
                          disabled={linhas.length <= 1}
                          onClick={() => setLinhas((ls) => ls.filter((_, j) => j !== i))}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn btn-sm btn-outline-theme" onClick={maisUmPedido} disabled={totalC > 0 && faltaC <= 0}>
                <i className="bi bi-plus-lg me-1"></i>Mais um pedido
              </button>
              {linhas.length === 1 && !linhas[0].valor && totalC > 0 && (
                <button type="button" className="btn btn-sm btn-link" onClick={() => mudarLinha(0, { valor: total })}>
                  O total é todo deste pedido
                </button>
              )}
              {/* A justificativa é nossa, como os pedidos: fica aqui, e continua
                  editável mesmo com a conta lançada no Tiny. */}
              <div className="form-floating mt-4">
                <textarea
                  id="oc-justificativa"
                  className="form-control"
                  style={{ height: 90 }}
                  placeholder=" "
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                />
                <label htmlFor="oc-justificativa">Justificativa</label>
              </div>
              <div className="form-text">Interna — não vai ao Tiny.</div>
            </div>
          </div>
          {barraSalvar}
        </fieldset>
      )}

      {aba === 'tiny' && (
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card adminuiux-card">
              <div className="card-body small">
                {!editando ? (
                  <p className="text-secondary mb-0">Salve a ordem de compra para mandá-la ao Tiny.</p>
                ) : oc?.envio_tiny === 'enviada' ? (
                  <p className="mb-0">
                    <i className="bi bi-check-circle-fill text-success me-1"></i>
                    Criada no Tiny: OC <strong>#{oc.numero_tiny}</strong>
                    {oc.enviado_tiny_em && ` em ${formatarData(oc.enviado_tiny_em)}`}.
                    {oc.contas_lancadas_tiny && (
                      <span className="d-block mt-2 text-secondary">
                        <i className="bi bi-lock me-1"></i>Conta lançada no Tiny — lá ela não muda mais.
                      </span>
                    )}
                  </p>
                ) : oc?.envio_tiny === 'verificar' || oc?.envio_tiny === 'enviando' ? (
                  <div className="alert alert-danger small mb-0">
                    <div className="fw-semibold mb-1">
                      <i className="bi bi-lock-fill me-1"></i>O Tiny não confirmou este envio
                    </div>
                    <p className="mb-2">Procure no Tiny antes de mandar de novo — pode ter entrado.</p>
                    {oc.erro_envio_tiny && <p className="mb-2 text-body-secondary">{oc.erro_envio_tiny}</p>}
                    {pode('custos-pedido.pedido.editar') && (
                      <>
                        <div className="input-group input-group-sm mb-2">
                          <input
                            className="form-control"
                            inputMode="numeric"
                            placeholder="Id da OC no Tiny"
                            value={idTiny}
                            onChange={(e) => setIdTiny(e.target.value.replace(/\D/g, ''))}
                          />
                          <button type="button" className="btn btn-success" disabled={!idTiny} onClick={() => reabrir(true)}>
                            Entrou
                          </button>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-secondary w-100" onClick={() => reabrir(false)}>
                          Conferi: não entrou
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <h6 className="mb-2">Para mandar ao Tiny</h6>
                    <ul className="list-unstyled mb-3">
                      {(previa?.pronto ?? []).map((p) => (
                        <li key={p.item} className="mb-1">
                          <i className={`bi me-2 ${p.ok ? 'bi-check-circle-fill text-success' : 'bi-circle text-secondary'}`}></i>
                          {p.item}
                        </li>
                      ))}
                    </ul>
                    {previa && !previa.escrita_ativa ? (
                      <p className="text-secondary mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        Nesta cópia o envio ao Tiny fica desligado — o envio de verdade é em produção.
                      </p>
                    ) : (
                      pode('custos-pedido.pedido.editar') && (
                        <button
                          type="button"
                          className="btn btn-theme w-100"
                          disabled={!!previa?.falta}
                          onClick={() => setConfirmar(true)}
                        >
                          <i className="bi bi-send me-1"></i>Mandar para o Tiny
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {editando && oc && (
            <div className="col-lg-7">
              <div className="card adminuiux-card">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0">O que vai ao Tiny</h6>
                </div>
                <div className="card-body small">
                  <dl className="row mb-0">
                    <dt className="col-4 text-secondary fw-normal">Item</dt>
                    <dd className="col-8">Item AOC · 1 × {fmt(oc.valor_total)}</dd>
                    <dt className="col-4 text-secondary fw-normal">Fornecedor</dt>
                    <dd className="col-8">{oc.fornecedor ?? '—'}</dd>
                    <dt className="col-4 text-secondary fw-normal">Parcelas</dt>
                    <dd className="col-8">
                      {oc.parcelas.length
                        ? oc.parcelas.map((p) => `${formatarData(p.vencimento)} ${fmt(p.valor)}`).join(' · ')
                        : 'nenhuma — definir no Tiny'}
                    </dd>
                    <dt className="col-4 text-secondary fw-normal">Observações internas</dt>
                    <dd className="col-8" style={{ whiteSpace: 'pre-line' }}>
                      {oc.observacoes_internas}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal className="casaa-modal" show={confirmar} onHide={() => setConfirmar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Mandar para o Tiny</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Isto cria uma <strong>ordem de compra de verdade</strong> no Tiny, de <strong>{fmt(totalC / 100)}</strong>.
            Se o Tiny não confirmar, ela fica com cadeado até alguém conferir.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setConfirmar(false)}>
            Cancelar
          </button>
          <button type="button" className="btn btn-theme" onClick={enviar}>
            Mandar
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
